import { Cancel, Search } from '@jsluna/icons';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

// eslint-disable-next-line import/prefer-default-export
var VARIANT = {
  FILLED: 'filled',
  LINK: 'link',
  OUTLINED: 'outlined',
  TEXT: 'text'
};

var baseClassName = 'ln-c-button';
/**
 * Base component extended by all other button variants
 */

var Button = function Button(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      disabled = _ref.disabled,
      fullWidth = _ref.fullWidth,
      onClick = _ref.onClick,
      type = _ref.type,
      innerRef = _ref.innerRef,
      circle = _ref.circle,
      hard = _ref.hard,
      role = _ref.role,
      variant = _ref.variant,
      color = _ref.color,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "disabled", "fullWidth", "onClick", "type", "innerRef", "circle", "hard", "role", "variant", "color"]);

  var buttonEl = element;
  var props = Object.assign({}, rest, {
    className: classnames(baseClassName, variant && baseClassName + "--" + variant, fullWidth && baseClassName + "--full", circle && baseClassName + "--circle", hard && baseClassName + "--hard", color && baseClassName + "--" + color, disabled && 'is-disabled', className),
    'aria-disabled': disabled || undefined,
    role: role,
    onClick: onClick,
    ref: innerRef
  });

  if (buttonEl === 'button') {
    props = Object.assign({}, props, {
      role: role === 'button' ? undefined : role,
      disabled: disabled,
      type: type
    });
  }

  return React.isValidElement(buttonEl) ? React.cloneElement(buttonEl, props, children) : React.createElement(buttonEl, props, children);
};

Button.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node.isRequired,

  /** Resets padding and adds border radius to be circular in shape */
  circle: PropTypes.bool,
  className: PropTypes.string,

  /** Colour scheme to be used */
  color: PropTypes.string,
  disabled: PropTypes.bool,

  /** Block level display to take up full width of container */
  fullWidth: PropTypes.bool,

  /** Removes padding from the ends of the button */
  hard: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),

  /** Determines the display style used e.g. filled / outlined  */
  variant: PropTypes.oneOf(Object.keys(VARIANT).map(function (key) {
    return VARIANT[key];
  }))
};
Button.defaultProps = {
  circle: false,
  className: undefined,
  disabled: false,
  element: 'button',
  fullWidth: false,
  innerRef: undefined,
  role: 'button',
  hard: false,
  type: 'button',
  variant: undefined,
  color: undefined
};
Button.displayName = 'Button';

/**
 * @see Extends [`Button`](#/Components/Button?id=button-1) by setting relevant `variant`
 */

var FilledButton = function FilledButton(_ref) {
  var children = _ref.children,
      rest = _objectWithoutPropertiesLoose(_ref, ["children"]);

  return /*#__PURE__*/React.createElement(Button, _extends({}, rest, {
    variant: VARIANT.FILLED
  }), children);
};

FilledButton.propTypes = {
  children: PropTypes.node.isRequired
};
FilledButton.displayName = 'FilledButton';

/**
 * @see Extends [`Button`](#/Components/Button?id=button-1) by adding setting relevant `variant`
 */

var TextButton = function TextButton(_ref) {
  var children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["children"]);

  return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
    variant: VARIANT.TEXT
  }), children);
};

TextButton.propTypes = {
  children: PropTypes.node.isRequired
};
TextButton.displayName = 'TextButton';

/**
 * @see Extends [`Button`](#/Components/Button?id=button-1) by setting relevant `variant`
 */

var LinkButton = function LinkButton(_ref) {
  var className = _ref.className,
      children = _ref.children,
      bare = _ref.bare,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "children", "bare"]);

  return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
    variant: VARIANT.LINK,
    className: classnames(bare && 'ln-c-button--link-bare', className)
  }), /*#__PURE__*/React.createElement("span", {
    className: "ln-c-button__underline"
  }, children));
};

LinkButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  /** Removes the default line decoration under LinkButton */
  bare: PropTypes.bool
};
LinkButton.defaultProps = {
  className: undefined,
  bare: false
};
LinkButton.displayName = 'LinkButton';

/**
 * @see Extends [`Button`](#/Components/Button?id=button-1) by setting relevant `variant`
 */

var OutlinedButton = function OutlinedButton(_ref) {
  var children = _ref.children,
      rest = _objectWithoutPropertiesLoose(_ref, ["children"]);

  return /*#__PURE__*/React.createElement(Button, _extends({}, rest, {
    variant: VARIANT.OUTLINED
  }), children);
};

OutlinedButton.propTypes = {
  children: PropTypes.node.isRequired
};
OutlinedButton.displayName = 'OutlinedButton';

var IconButton = function IconButton(_ref) {
  var children = _ref.children,
      className = _ref.className,
      hideLabel = _ref.hideLabel,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "hideLabel", "label"]);

  return /*#__PURE__*/React.createElement(Button, _extends({}, rest, {
    className: classnames(hideLabel ? 'ln-c-button--icon' : 'ln-c-button--icon-text', className)
  }), React.cloneElement(React.Children.only(children), {
    className: 'ln-c-button__icon'
  }), hideLabel ? /*#__PURE__*/React.createElement("span", {
    className: "ln-u-visually-hidden"
  }, label) : label);
};

IconButton.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,

  /** Text content to show along side icon to give greater context */
  label: PropTypes.node.isRequired,

  /** Whether the text should only be available to screen readers */
  hideLabel: PropTypes.bool
};
IconButton.defaultProps = {
  children: undefined,
  className: undefined,
  hideLabel: false
};
IconButton.displayName = 'IconButton';

/**
 * Helper button used primarily inside form controls that makes use of the
 * [`Cancel`](#/Icons) icon
 * @see Extends [`Button`](#/Components/Button?id=button-1)
 */

var CancelButton = function CancelButton(props) {
  return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
    hard: true
  }), /*#__PURE__*/React.createElement(Cancel, null));
};

CancelButton.displayName = 'CancelButton';

/**
 * @see Extends [`CancelButton`](#/Components?id=cancelbutton) with preset style `className` &
 * `title` for use with dismissable components
 */

var CloseButton = function CloseButton(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "onClick", "label"]);

  return /*#__PURE__*/React.createElement(CancelButton, _extends({}, rest, {
    className: classnames(className, 'ln-c-dismiss'),
    onClick: onClick,
    "aria-label": label
  }));
};

CloseButton.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string
};
CloseButton.defaultProps = {
  className: undefined,
  label: 'Close'
};
CloseButton.displayName = 'CloseButton';

/**
 * Helper button to be used in combination with form inputs that makes use of the
 * [`Search`](#/Icons) icon
 * @see Extends [`Button`](#/Components/Button?id=button-1)
 */

var SearchButton = function SearchButton(_ref) {
  var icon = _ref.icon,
      inputAction = _ref.inputAction,
      label = _ref.label,
      rest = _objectWithoutPropertiesLoose(_ref, ["icon", "inputAction", "label"]);

  return /*#__PURE__*/React.createElement(IconButton, _extends({
    variant: !inputAction ? VARIANT.FILLED : undefined,
    type: "submit",
    label: label,
    hideLabel: true
  }, rest), icon);
};

SearchButton.propTypes = {
  icon: PropTypes.node,
  inputAction: PropTypes.bool,
  label: PropTypes.string
};
SearchButton.defaultProps = {
  /** Icon to be displayed */
  icon: /*#__PURE__*/React.createElement(Search, null),

  /** Applies the bare button styling */
  inputAction: false,

  /** Text description for screen readers */
  label: 'Search'
};
SearchButton.displayName = 'SearchButton';

var ButtonGroupWrapper = function ButtonGroupWrapper(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      actionBar = _ref.actionBar,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "actionBar"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames(className, 'ln-c-button-group', actionBar ? 'ln-c-button-group--action-bar' : 'ln-o-clearfix')
  }), children);
};

ButtonGroupWrapper.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  actionBar: PropTypes.bool
};
ButtonGroupWrapper.defaultProps = {
  element: 'div',
  className: undefined,
  actionBar: false
};
ButtonGroupWrapper.displayName = 'ButtonGroupWrapper';

var ButtonGroupPrimary = function ButtonGroupPrimary(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames(className, 'ln-c-button-group__primary')
  }), children);
};

ButtonGroupPrimary.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};
ButtonGroupPrimary.defaultProps = {
  element: 'div',
  className: undefined
};
ButtonGroupPrimary.displayName = 'ButtonGroupPrimary';

var ButtonGroupSecondary = function ButtonGroupSecondary(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames(className, 'ln-c-button-group__secondary')
  }), children);
};

ButtonGroupSecondary.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};
ButtonGroupSecondary.defaultProps = {
  element: 'div',
  className: undefined
};
ButtonGroupSecondary.displayName = 'ButtonGroupSecondary';

// Base

export { Button, FilledButton, TextButton, LinkButton, OutlinedButton, IconButton, CancelButton, CloseButton, SearchButton, ButtonGroupWrapper, ButtonGroupPrimary, ButtonGroupSecondary };
//# sourceMappingURL=index.js.map
